import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useInventoryItems } from "../../api/inventory";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FilterBar } from "../../components/ui/FilterBar";
import { Page } from "../../components/ui/Page";
import { Pagination } from "../../components/ui/Pagination";
import { Spinner } from "../../components/ui/Spinner";
import { Table } from "../../components/ui/Table";
import { useLayoutTitle } from "../../hooks/useLayoutTitle";
import { usePaginationParams } from "../../hooks/usePaginationParams";
import { useSortParams } from "../../hooks/useSortParams";
import { useTranslation } from "../../hooks/useTranslation";
import { statusLabel } from "../../types/inventory";
import type { InventoryAudience, InventoryItemResponse } from "../../types/inventory";

const PAGE_SIZE = 20;

type StatusFilter = "all" | "under_review" | "taken_over" | "rejected" | "completed";
type AudienceFilter = "" | InventoryAudience;

export default function InventoryListPage() {
  const { t, language } = useTranslation();
  useLayoutTitle(t("inventory.allItems"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFilter = (searchParams.get("status") ?? "all") as StatusFilter;
  const search = searchParams.get("search")?.trim() || undefined;
  const mine = searchParams.get("mine") === "true";
  const locked = searchParams.get("locked") === "1";
  const handleStatusChange = (value: StatusFilter) => {
    setSearchParams((prev) => {
      if (value === "all") prev.delete("status");
      else prev.set("status", value);
      prev.set("page", "1");
      return prev;
    });
  };

  const audience = (searchParams.get("audience") ?? "") as AudienceFilter;
  const handleAudienceChange = (value: AudienceFilter) => {
    setSearchParams((prev) => {
      if (!value) prev.delete("audience");
      else prev.set("audience", value);
      prev.set("page", "1");
      return prev;
    });
  };

  type SortKey = "name" | "createdAt";
  const { sortBy, sortDir, setSort } = useSortParams<SortKey>("createdAt");

  const rawPage = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10));
  const { data, isLoading } = useInventoryItems({
    status: statusFilter === "all" ? undefined : statusFilter,
    audience: audience || undefined,
    search,
    sortBy,
    sortDir,
    page: rawPage,
    limit: PAGE_SIZE,
    mine: mine || undefined,
  });

  const [items, setItems] = useState<InventoryItemResponse[]>([]);
  useEffect(() => {
    data?.data && setItems(data.data);
  }, [data?.data]);
  const { page, totalPages, setPage } = usePaginationParams(PAGE_SIZE, data?.total ?? 0);

  const statusFilters: { value: StatusFilter; label: string }[] = [
    { value: "all", label: t("inventory.filterAll") },
    { value: "under_review", label: t("inventory.filterUnderReview") },
    { value: "taken_over", label: t("inventory.filterTakenOver") },
    { value: "completed", label: t("inventory.filterCompleted") },
    { value: "rejected", label: t("inventory.filterRejected") },
  ];

  const audienceFilters: { value: InventoryAudience; label: string }[] = [
    { value: "internal", label: t("inventory.audienceInternal") },
    { value: "external", label: t("inventory.audienceExternal") },
  ];

  return (
    <Page>
      <Page.TitleRow>
        <Page.FilterRow>
          <FilterBar value={statusFilter} options={statusFilters} onChange={handleStatusChange} disabled={locked} />
          <Page.FilterDivider />
          <FilterBar<AudienceFilter>
            value={audience}
            options={audienceFilters}
            onChange={handleAudienceChange}
            nullable
          />
          {isLoading && <Spinner />}
        </Page.FilterRow>
        <Link to="/inventory/new">
          <Button>+</Button>
        </Link>
      </Page.TitleRow>

      {items.length === 0 ? (
        <EmptyState>{t("inventory.noItems")}</EmptyState>
      ) : (
        <>
          <Table>
            <Table.Head>
              <Table.HeaderCell sortDir={sortBy === "name" ? sortDir : null} onSort={() => setSort("name")}>
                {t("inventory.name")}
              </Table.HeaderCell>
              <Table.HeaderCell>{t("inventory.audience")}</Table.HeaderCell>
              <Table.HeaderCell>{t("inventory.status")}</Table.HeaderCell>
              <Table.HeaderCell sortDir={sortBy === "createdAt" ? sortDir : null} onSort={() => setSort("createdAt")}>
                {t("inventory.createdAt")}
              </Table.HeaderCell>
            </Table.Head>
            <Table.Body>
              {items.map((item) => (
                <Table.Row key={item.id} onClick={() => navigate(`/inventory/${item.id}`)}>
                  <Table.Cell variant="strong">{item.name}</Table.Cell>
                  <Table.Cell>
                    <Badge
                      variant={item.audience}
                      label={item.audience === "external" ? t("inventory.audienceExternal") : t("inventory.audienceInternal")}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant={item.status} label={statusLabel(item.status, t)} />
                  </Table.Cell>
                  <Table.Cell variant="muted">{new Date(item.createdAt).toLocaleDateString(language)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}
    </Page>
  );
}
